import styled from 'styled-components';
import rem from 'polished/lib/helpers/rem';
import toColorString from 'polished/lib/color/toColorString';
import color from '@decisiv/design-tokens/lib/color';
import spacing from '@decisiv/design-tokens/lib/spacing';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Breadcrumbs from '@decisiv/ui-components/lib/components/Breadcrumbs';
import Button from '@decisiv/ui-components/lib/components/Button';
import { H3, P } from '@decisiv/ui-components/lib/components/Typography';

const fullMoon = toColorString(color.base.fullMoon);
const halfMoon = toColorString(color.base.halfMoon);
const snowWhite = toColorString(color.base.snowWhite);
const charcoal = toColorString(color.base.charcoal);
const alaskanHusky = toColorString(color.base.alaskanHusky);
const blackSheep = toColorString(color.base.blackSheep);
const informationLight = toColorString(color.status.information.light);
const informationDark = toColorString(color.status.information.dark);
const successMedium = toColorString(color.status.success.medium);
const warningMedium = toColorString(color.status.warning.medium);
const warningLight = toColorString(color.status.warning.light);
const warningDark = toColorString(color.status.warning.dark);
const dangerDark = toColorString(color.status.danger.dark);

export const PageSection = styled(Flex).attrs({
  flexDirection: 'column',
  justifyContent: 'center',
  padding: 2,
  flex: 'none',
})`
  background: ${snowWhite};
`;

export const PageSectionHeader = styled(Flex).attrs({
  flexDirection: 'row',
  alignItems: 'center',
})``;

export const LineDivider = styled.div`
  width: 100%;
  height: ${rem(spacing.base * 0.1)};
  background: ${toColorString(color.opacity.charcoal15)};
`;

export const ListContainer = styled(Flex).attrs({
  flexDirection: 'column',
  padding: 2,
})`
  background: ${snowWhite};
`;

export const SettingsTable = styled.div`
  border: 1px solid ${fullMoon};
  border-radius: ${rem(6)};
  overflow: hidden;
`;

const TABLE_GRID = `
  display: grid;
  grid-template-columns: 44px minmax(160px, 2fr) minmax(120px, 1fr) minmax(110px, 1fr) minmax(110px, 1fr) 72px;
  align-items: center;
  gap: ${rem(spacing.base)};
  padding: 0 ${rem(spacing.base * 1.5)};
`;

export const TableHeader = styled.div`
  ${TABLE_GRID}
  padding-top: ${rem(spacing.base * 0.75)};
  padding-bottom: ${rem(spacing.base * 0.75)};
  background-color: ${halfMoon};
  border-bottom: 2px solid ${fullMoon};
`;

export const TableRow = styled.div`
  ${TABLE_GRID}
  padding-top: ${rem(spacing.base)};
  padding-bottom: ${rem(spacing.base)};
  background-color: ${snowWhite};
  border-bottom: 1px solid ${fullMoon};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${halfMoon};
  }
`;

export const TableHeaderCell = styled.div`
  font-size: ${rem(11)};
  font-weight: 700;
  color: ${alaskanHusky};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  white-space: nowrap;
`;

export const AvatarSquare = styled.div`
  width: ${rem(36)};
  height: ${rem(36)};
  border-radius: ${rem(8)};
  background-color: ${({ $color }) => $color};
  color: var(--if-on-vivid);
  font-size: ${rem(12)};
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  letter-spacing: 0.03em;
  user-select: none;
`;

export const NameCell = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const NamePrimary = styled.span`
  font-size: ${rem(14)};
  font-weight: 600;
  color: ${charcoal};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const NameSubtitle = styled.span`
  font-size: ${rem(12)};
  color: ${alaskanHusky};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: ${rem(2)};
`;

export const IntegrationNamesCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
  min-width: 0;
`;

export const IntegrationName = styled.span`
  font-size: ${rem(12)};
  color: ${({ $muted }) => ($muted ? alaskanHusky : charcoal)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const DateStack = styled.div`
  display: flex;
  flex-direction: column;
`;

export const DatePrimary = styled.span`
  font-size: ${rem(13)};
  color: ${charcoal};
`;

export const DateRelative = styled.span`
  font-size: ${rem(11)};
  color: ${alaskanHusky};
  margin-top: ${rem(2)};
`;

export const TableFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${rem(spacing.base)} ${rem(spacing.base * 1.5)};
  border-top: 1px solid ${fullMoon};
  background-color: ${snowWhite};
`;

export const ShowingText = styled.span`
  font-size: ${rem(13)};
  color: ${alaskanHusky};
`;

export const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${rem(spacing.base * 0.5)};
`;

// Form layout
export const FormContainer = styled(Flex).attrs({
  flexDirection: 'column',
  padding: 2,
})`
  max-width: ${rem(900)};
`;

export const FormSection = styled(Flex).attrs({
  flexDirection: 'column',
  marginBottom: 2,
})``;

export const InputWrapper = styled(Flex).attrs({
  flexDirection: 'column',
})`
  max-width: ${rem(400)};
`;

export const SelectedIntegrationRow = styled(Flex).attrs({
  alignItems: 'center',
  paddingX: 1,
  paddingY: 0.5,
  marginBottom: 0.25,
})`
  background-color: ${halfMoon};
  border: 1px solid ${fullMoon};
  border-radius: ${rem(4)};
  max-width: ${rem(500)};
`;

export const SearchResultsContainer = styled(Flex).attrs({
  flexDirection: 'column',
  marginTop: 0.75,
})`
  border: 1px solid ${fullMoon};
  border-radius: ${rem(4)};
  max-width: ${rem(500)};
  overflow: hidden;
`;

export const SearchResultRow = styled(Flex).attrs({
  alignItems: 'center',
  paddingX: 1,
  paddingY: 0.5,
})`
  border-bottom: 1px solid ${fullMoon};
  background-color: ${snowWhite};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${halfMoon};
  }
`;

export const AttrName = styled.span`
  font-family: monospace;
  font-size: ${rem(12)};
  font-weight: 600;
  color: ${charcoal};
  white-space: nowrap;
`;

export const KeySeparator = styled.span`
  color: ${alaskanHusky};
  font-size: ${rem(12)};
  flex-shrink: 0;
  padding: 0 ${rem(2)};
`;

export const SourceKeyCell = styled(Flex).attrs({
  alignItems: 'center',
})`
  gap: ${rem(spacing.base * 0.5)};

  button,
  [role='button'] {
    text-transform: none;
    font-family: inherit;
    font-size: ${rem(13)};
    letter-spacing: normal;
  }
`;

export const MappingFieldWrapper = styled(Flex).attrs({
  flexDirection: 'column',
})`
  flex: 1;
  min-width: 0;
`;

export const SectionLabel = styled.p`
  font-size: ${rem(12)};
  font-weight: 600;
  color: ${alaskanHusky};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0 0 ${rem(8)} 0;
`;

// Pairwise mapping card
export const MappingCard = styled(Flex).attrs({
  flexDirection: 'column',
  marginBottom: 1.5,
})`
  border: 1px solid ${fullMoon};
  border-radius: ${rem(4)};
  overflow: hidden;
`;

export const MappingCardHeader = styled(Flex).attrs({
  alignItems: 'center',
  paddingX: 1.5,
  paddingY: 0.75,
})`
  background-color: ${halfMoon};
  border-bottom: 1px solid ${fullMoon};
  font-weight: 600;
  font-size: ${rem(13)};
`;

// One collapsible section per source integration. Without this the step renders
// every pairwise combination at once — 36 cards for the 9-integration group.
export const MappingGroup = styled.div`
  border: 1px solid ${fullMoon};
  border-radius: ${rem(4)};
  overflow: hidden;
  margin-bottom: ${rem(spacing.base * 0.5)};

  &:last-of-type {
    margin-bottom: 0;
  }
`;

export const MappingGroupHead = styled.button.attrs({ type: 'button' })`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${rem(spacing.base * 0.75)};
  padding: ${rem(spacing.base * 0.75)} ${rem(spacing.base)};
  background: ${halfMoon};
  border: 0;
  cursor: pointer;
  text-align: left;
  font-family: inherit;

  &:hover {
    background: ${fullMoon};
  }
`;

export const GroupCaret = styled.span`
  flex: none;
  display: flex;
  align-items: center;
  transform: rotate(${({ expanded }) => (expanded ? '90deg' : '0deg')});
  transition: transform 0.16s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const GroupName = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  font-size: ${rem(13)};
  font-weight: 600;
  color: ${charcoal};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const GroupSub = styled.span`
  flex: none;
  font-size: ${rem(12)};
  color: ${alaskanHusky};
`;

export const MappingGroupPanel = styled.div`
  padding: ${rem(spacing.base * 0.75)} ${rem(spacing.base)} ${rem(spacing.base)};
  border-top: 1px solid ${fullMoon};
  background: ${snowWhite};
`;

export const MappingRow = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 3fr) 36px minmax(180px, 2fr) minmax(
      140px,
      1.5fr
    );
  align-items: center;
  padding: 0.75rem 1.25rem;
  gap: 1rem;
  border-bottom: 1px solid ${fullMoon};
  background-color: ${snowWhite};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${halfMoon};
  }
`;

export const MappingSelectWrap = styled.div`
  min-width: 0;

  /* Force SelectMenu selected-value text to normal case */
  button,
  [role='button'] {
    text-transform: none;
    font-family: inherit;
    font-size: ${rem(13)};
    letter-spacing: normal;
  }
`;

export const AddMappingRow = styled(Flex).attrs({
  paddingX: 1.5,
  paddingY: 0.75,
})`
  background-color: ${halfMoon};
`;

export const ArrowLabel = styled.span`
  font-size: ${rem(16)};
  color: ${alaskanHusky};
  margin: 0 ${rem(8)};
  flex-shrink: 0;
`;

// Two-column page layout: main content + sticky right rail
export const PageLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 280px;
  gap: 1.5rem;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

export const FormCard = styled.div`
  background: ${snowWhite};
  border: 1px solid ${fullMoon};
  border-radius: 8px;
  box-shadow: 0 1px 2px color-mix(in srgb, var(--if-shadow) 12%, transparent);
`;

export const DesignSection = styled.div`
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 2rem;
  padding: 1.5rem 1.75rem;

  & + & {
    border-top: 1px solid ${fullMoon};
  }
`;

export const DesignSectionHead = styled.div``;

export const StepBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${informationLight};
  color: ${informationDark};
  font-size: 11px;
  font-weight: 700;
  margin-right: 8px;
  flex-shrink: 0;
`;

export const FormTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.125rem 0.25rem 0.875rem 0.75rem;
`;

export const ChipShelf = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 12px;
  border: 1px dashed ${fullMoon};
  border-radius: 6px;
  background: ${halfMoon};
  min-height: 50px;
  align-items: center;
  margin-top: 12px;
`;

export const IntegrationChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 6px 5px 10px;
  border-radius: 6px;
  background: ${snowWhite};
  border: 1px solid ${fullMoon};
  font-size: 13px;
  font-weight: 500;
  color: ${charcoal};
  box-shadow: 0 1px 0 color-mix(in srgb, var(--if-shadow) 10%, transparent);
`;

export const PrimaryBadge = styled.span`
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 3px;
  background: ${informationLight};
  color: ${informationDark};
  white-space: nowrap;
`;

// Marks a linked integration as "Attributes/Lifecycle" (it takes part in
// attribute mapping) or "Lifecycle" (no location-level attributes to map, so it
// only keeps its location lifecycle state aligned with its peers).
export const SyncBadge = styled.span`
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 3px;
  white-space: nowrap;
  background: ${({ lifecycleOnly }) =>
    lifecycleOnly ? fullMoon : informationLight};
  color: ${({ lifecycleOnly }) =>
    lifecycleOnly ? alaskanHusky : informationDark};
`;

export const MappingsNotice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid ${fullMoon};
  border-radius: 6px;
  background: ${halfMoon};
  margin-bottom: 12px;
`;

// type="button" is required, not cosmetic: a bare <button> defaults to
// type="submit", which makes it the enclosing form's default button. Pressing
// Enter in any input then fires a click on it via implicit form submission.
export const MakePrimaryBtn = styled.button.attrs({ type: 'button' })`
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 3px;
  background: ${halfMoon};
  color: ${alaskanHusky};
  border: 0;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: ${fullMoon};
  }
`;

// See MakePrimaryBtn — without type="button" this chip's remove control becomes
// the form's default button, so Enter in the integration search deletes the
// first selected integration.
export const ChipXBtn = styled.button.attrs({ type: 'button' })`
  appearance: none;
  border: 0;
  background: transparent;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  cursor: pointer;
  color: ${alaskanHusky};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: var(--if-danger-soft);
    color: var(--if-danger-ink);
  }
`;

export const ResultSub = styled.div`
  font-size: 11.5px;
  color: ${alaskanHusky};
  margin-top: 1px;
`;

export const MappingsToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

export const CountPill = styled.span`
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
  letter-spacing: 0.03em;
  background: ${({ warn }) =>
    warn ? 'var(--if-warning-soft)' : 'var(--if-success-soft)'};
  color: ${({ warn }) =>
    warn ? 'var(--if-warning-ink)' : 'var(--if-success-ink)'};
`;

export const RailSidebar = styled.div`
  position: sticky;
  top: 1rem;
  align-self: start;
`;

export const RailCard = styled.div`
  background: ${snowWhite};
  border: 1px solid ${fullMoon};
  border-radius: 8px;
  padding: 1.125rem;
  margin-bottom: 14px;
`;

export const RailTitle = styled.p`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${alaskanHusky};
  margin: 0 0 12px 0;
`;

export const ChecklistRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 13px;
  margin-bottom: 10px;
`;

const checkDotBackground = ({ done, warn }) => {
  if (done) return successMedium;
  if (warn) return warningMedium;
  return snowWhite;
};

const checkDotBorder = ({ done, warn }) => {
  if (done) return successMedium;
  if (warn) return warningMedium;
  return fullMoon;
};

export const CheckDot = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${checkDotBackground};
  border: 2px solid ${checkDotBorder};
  color: ${snowWhite};
  font-size: 10px;
`;

export const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 5px 0;
  font-size: 12.5px;
  color: ${charcoal};
  border-top: 1px solid ${fullMoon};
`;

export const ProgressTrack = styled.div`
  height: 6px;
  border-radius: 3px;
  background: ${fullMoon};
  overflow: hidden;
  margin: 10px 0 4px;
`;

export const ProgressFill = styled.div`
  height: 100%;
  background: ${informationDark};
  transition: width 0.3s ease;
  width: ${({ pct }) => pct}%;
`;

/* ---- Shared Setting form ---------------------------------------------------
   Blocks the form used to compose inline. `spacing.base` is 10, so
   rem(spacing.base * n) reads as n * 10px. The colours were hand-picked hex
   carried over from the original single-file form; these are the nearest KDS
   status tokens, so a few shades shift slightly on purpose. */

export const StepHeading = styled(H3)`
  margin: 0 0 ${rem(spacing.base * 0.6)};
  display: flex;
  align-items: center;
`;

export const StepIntro = styled(P).attrs({ shade: 1, size: 'small' })`
  margin: 0;
`;

export const FormPageTitle = styled(H3)`
  margin: 0;
  font-size: ${rem(spacing.base * 2.4)};
`;

// Nudge next to "Selected integrations (1)" — one short of the minimum.
export const SelectionNudge = styled.span`
  margin-left: ${rem(spacing.base * 0.8)};
  color: ${warningDark};
  text-transform: none;
  letter-spacing: normal;
  font-weight: 600;
  font-size: ${rem(spacing.base * 1.2)};
`;

export const FieldError = styled(P).attrs({ size: 'small', marginTop: 0.5 })`
  color: ${dangerDark};
`;

export const SearchResultName = styled(P).attrs({
  size: 'small',
  marginBottom: 0,
})`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
`;

// "At least one attribute mapping is required" — the only blocking state in the
// mapping step, so it carries the full warning treatment rather than a notice.
export const MappingWarning = styled(Flex).attrs({
  alignItems: 'flex-start',
  marginBottom: 1,
})`
  background: ${warningLight};
  border: ${rem(spacing.base * 0.1)} solid ${warningMedium};
  border-radius: ${rem(spacing.base * 0.4)};
  padding: ${rem(spacing.base * 0.8)} ${rem(spacing.base * 1.2)};
  gap: ${rem(spacing.base * 0.8)};

  > svg {
    flex-shrink: 0;
    margin-top: ${rem(spacing.base * 0.2)};
  }
`;

export const MappingWarningText = styled(P).attrs({
  size: 'small',
  marginBottom: 0,
})`
  color: ${warningDark};
`;

export const TipsList = styled.ul`
  margin: 0 0 0 ${rem(spacing.base * 1.6)};
  padding: 0;
  font-size: ${rem(spacing.base * 1.25)};
  line-height: 1.55;
  color: ${charcoal};
`;

// The label only needs separating from the search results once chips exist
// above it, so the gap is a variant rather than a conditional inline style.
export const SelectedLabel = styled(SectionLabel)`
  margin-top: ${({ spaced }) => (spaced ? rem(spacing.base * 1.6) : '0')};
`;

export const BreadcrumbsBar = styled(Breadcrumbs)`
  padding: ${rem(spacing.base * 0.6)} ${rem(spacing.base * 2.1)};
`;

export const TitleActionsRow = styled(Flex).attrs({ alignItems: 'flex-end' })`
  gap: ${rem(spacing.base * 1.6)};
`;

export const NameFieldCell = styled.div`
  flex: 1;
`;

export const FormActions = styled(Flex)`
  gap: ${rem(spacing.base * 0.8)};
  flex-shrink: 0;
`;

// min-width: 0 lets the name below actually ellipsize inside the flex row.
export const SearchResultInfo = styled(Flex).attrs({
  flexDirection: 'column',
  flex: 1,
})`
  min-width: 0;
`;

export const SearchResultMeta = styled(Flex).attrs({ alignItems: 'center' })`
  gap: ${rem(spacing.base * 0.6)};
  margin-top: ${rem(spacing.base * 0.2)};
`;

export const AddResultButton = styled(Button)`
  flex-shrink: 0;
  margin-left: ${rem(spacing.base * 0.8)};
`;

export const RailRowTitle = styled(P).attrs({ size: 'small' })`
  font-weight: 500;
  margin: 0;
`;

export const RailRowSub = styled(P).attrs({ size: 'small', shade: 1 })`
  margin: 0;
`;

export const RailSectionTitle = styled(P)`
  font-weight: 600;
  font-size: ${rem(spacing.base * 1.4)};
  margin: ${rem(spacing.base * 1.6)} 0 ${rem(spacing.base * 0.6)};
`;

// Exported so the icon components, which take a colour prop rather than a
// class, can use the same token as the surrounding block.
export { warningDark as mappingWarningColor };

// Export charcoal token so JSX can reference it directly for inline styles
export { charcoal as charcoalColor, blackSheep };

/* ---- Needs Attention -------------------------------------------------------
   Same shell the Integration Health page uses: tokens exposed as CSS custom
   properties so children style through var(), a centred body, and a pill view
   switch. Kept local to this page rather than imported across pages. */

const needsAttentionTokens = `
  --bg: var(--if-bg);
  --surface: var(--if-surface-1);
  --border: var(--if-border);
  --border-strong: var(--if-border-strong);
  --ink: var(--if-ink-1);
  --muted: var(--if-ink-2);
  --accent: var(--if-accent);
  --warn-soft: var(--if-warning-soft);
  --warn: var(--if-warning-ink);
  --good-soft: var(--if-success-soft);
  --good: var(--if-success-ink);
  --shadow-sm: 0 1px 2px color-mix(in srgb, var(--if-shadow) 15%, transparent);
`;

export const NeedsPage = styled.div`
  ${needsAttentionTokens}
  width: 100%;
  min-height: calc(100vh - 60px);
  background: var(--bg);
  color: var(--ink);
`;

export const NeedsBody = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 20px 48px;
`;

export const NeedsHeader = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 18px 20px 14px;
`;

export const NeedsControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

export const ViewSwitch = styled.div`
  display: inline-flex;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 3px;
`;

export const ViewButton = styled.button`
  border: 0;
  background: transparent;
  cursor: pointer;
  font: 650 12.5px inherit;
  color: var(--muted);
  padding: 7px 15px;
  border-radius: 999px;

  &[aria-pressed='true'] {
    background: var(--accent);
    color: var(--if-on-accent);
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
`;

export const NeedsCount = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  color: var(--muted);
`;

export const NeedsCard = styled.section`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: var(--shadow-sm);
  margin-bottom: 12px;
  overflow: hidden;
`;

export const NeedsCardHead = styled.header`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px 16px;
  border-bottom: 1px solid var(--border);

  .name {
    font-weight: 700;
    font-size: 14.5px;
  }

  .count {
    margin-left: auto;
    font-size: 12px;
    font-weight: 650;
    color: var(--warn);
    background: var(--warn-soft);
    border-radius: 999px;
    padding: 3px 10px;
    white-space: nowrap;
  }
`;

export const NeedsEmpty = styled.div`
  padding: 56px 20px;
  text-align: center;
  color: var(--muted);

  .mark {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: var(--good-soft);
    color: var(--good);
    display: grid;
    place-items: center;
    margin: 0 auto 14px;
    font-size: 26px;
  }
`;
